import { useQuery } from '@tanstack/react-query';
import { chatbotApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { toCancelToken } from 'src/api/queries/queryClient';

// Server-side aggregations over a date range; refetching them on every mount
// is expensive and the numbers barely move within a session.
const STATS_STALE_TIME = 5 * 60_000;

export interface AdminStatsRange {
  userId?: number;
  from: Date | null;
  to: Date | null;
}

export function useAdminStatsOverview({ userId, from, to }: AdminStatsRange) {
  return useQuery({
    queryKey: queryKeys.chatAdmin.stats({ kind: 'overview', userId, from, to }),
    queryFn: ({ signal }) =>
      chatbotApiClient.getStatsOverview(
        userId,
        from,
        to,
        toCancelToken(signal),
      ),
    enabled: !!from && !!to,
    staleTime: STATS_STALE_TIME,
    meta: { errorContext: 'AI assistant stats' },
  });
}

export function useAdminStatsDaily({ userId, from, to }: AdminStatsRange) {
  return useQuery({
    queryKey: queryKeys.chatAdmin.stats({ kind: 'daily', userId, from, to }),
    queryFn: ({ signal }) =>
      chatbotApiClient.getStatsDaily(userId, from, to, toCancelToken(signal)),
    enabled: !!from && !!to,
    staleTime: STATS_STALE_TIME,
    meta: { errorContext: 'AI assistant stats' },
  });
}

export function useAdminStatsByUser(from: Date | null, to: Date | null) {
  return useQuery({
    queryKey: queryKeys.chatAdmin.stats({ kind: 'byUser', from, to }),
    queryFn: ({ signal }) =>
      chatbotApiClient.getStatsByUser(from, to, toCancelToken(signal)),
    enabled: !!from && !!to,
    staleTime: STATS_STALE_TIME,
    meta: { errorContext: 'AI assistant top users' },
  });
}
